import type { ToothBasisSeriesFrame } from '@/shared/graphics/medicalImageRender/MedicalImageRender';
import type { Guide as RCGuideLine } from '@/shared/graphics/RenderComponents/RCGuides';

import { GUIDE_AXIS_EPSILON } from '../config';

export const clipLineToFrame = ({
  a,
  b,
  c,
  frame,
  tileID,
}: {
  a: number;
  b: number;
  c: number;
  frame: ToothBasisSeriesFrame;
  tileID: string;
}): RCGuideLine | null => {
  const halfWidthMm = (frame.widthPx * frame.spacingMm) / 2;
  const halfHeightMm = (frame.heightPx * frame.spacingMm) / 2;
  const clipEpsilon = frame.spacingMm / 2;

  const verticalEdgeCandidates =
    Math.abs(b) > GUIDE_AXIS_EPSILON
      ? [-halfWidthMm, halfWidthMm].flatMap((u) => {
          const v = (c - a * u) / b;

          return Math.abs(v) <= halfHeightMm + clipEpsilon ? [{ u, v }] : [];
        })
      : [];
  const horizontalEdgeCandidates =
    Math.abs(a) > GUIDE_AXIS_EPSILON
      ? [-halfHeightMm, halfHeightMm].flatMap((v) => {
          const u = (c - b * v) / a;

          return Math.abs(u) <= halfWidthMm + clipEpsilon ? [{ u, v }] : [];
        })
      : [];
  const candidates = [...verticalEdgeCandidates, ...horizontalEdgeCandidates];

  const points = candidates.filter(
    (point, index) =>
      candidates.findIndex(
        (other) =>
          Math.abs(other.u - point.u) < GUIDE_AXIS_EPSILON &&
          Math.abs(other.v - point.v) < GUIDE_AXIS_EPSILON,
      ) === index,
  );

  if (points.length < 2) {
    return null;
  }

  const [start, end] = points
    .flatMap((from, fromIndex) =>
      points.slice(fromIndex + 1).map((to) => ({
        from,
        to,
        lengthSq: (to.u - from.u) ** 2 + (to.v - from.v) ** 2,
      })),
    )
    .sort((left, right) => right.lengthSq - left.lengthSq)
    .map((pair) => [pair.from, pair.to])[0];

  const toPixelX = (u: number) => frame.widthPx / 2 + u / frame.spacingMm;
  const toPixelY = (v: number) => frame.heightPx / 2 - v / frame.spacingMm;

  return {
    assetID: tileID,
    x1: toPixelX(start.u),
    y1: toPixelY(start.v),
    x2: toPixelX(end.u),
    y2: toPixelY(end.v),
  };
};
